import { VdToastNotificationService } from '@vend/vend.ui'
import angular, { IPromise } from 'angular'
import ngRedux from 'ng-redux'
import some from 'lodash/some'
import map from 'lodash/map'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import VendNumber, { vn, isFinite, add, subtract, round } from 'vend-number'
import debouncePromise from 'debounce-promise'
import { VdTracking, VdFeatureManager } from '@vend/vend.js'
import {
  ApiProduct,
  ApiCustomer,
  ApiRetailerPaymentType,
  ApiRegister,
  ApiOutlet,
} from '@vend/types'

import uuid from '@vend/vend-uuid'
import { isValidEmail } from '@vend/business-components'
import { FetchResult } from '@apollo/client'
import {
  getFromStorage,
  getStorageEstimate,
  IncCounter,
  removeFromStorage,
  StartTimer,
  Timer,
  trackError,
} from '@vend/utilities'
import { RootState } from '../state/store'
import { PROMO_CODE_STATUES } from '../components/promotions/promotions.constant'
import {
  LineItemInput,
  LineItem,
  LineItemUser,
  createLineItem,
} from '../models/line-item'
import {
  ExternalPaymentData,
  createChangePayment,
  createPayment,
} from '../models/payment'
import {
  CustomerBalances,
  ReceiptNumberDetails,
  Sale,
  SurchargeTenderProperties,
} from '../models/sale'
import {
  getPromotionSummary,
  isSalePickable,
  apiUserToSaleUser,
  hasFulfilmentsCompleted,
  hasFulfilmentDetails,
  hasLineItemFulfilment,
  isEmptyParkedSale,
  isEmptySale,
  isCompleted,
  isConfirmed,
  isConfirmedSale,
  isActiveSale,
  isReturn,
  isValidCustomer,
  getSurchargeLabel,
  isClosed,
  isLayby,
  isOnAccount,
  isQuote,
  hasLineItems,
  hasUnknownLineItems,
  isBlindReturn,
  isUploadedToBeCompleted,
  hasPayments,
  isAccountBalancePaymentAdded,
  getPaymentsTotal,
  hasSurchargeRefundable,
  getOutstandingBalance,
  isConfirmedReadyToComplete,
  isConfirmedNotReadyToComplete,
  isMarkedAsUnfulfilled,
  isNonFulfillmentOnAccountSale,
  isUnfulfilled,
} from '../functions/sale'
import {
  SaleStatus,
  SaleSyncStatus,
  PARK_SALE_FAILED_ERROR_MSG,
} from '../models/sale.constants'
import { TaxesStore } from '../factories/database/taxes-store'
import { paymentTypesStore } from '../factories/database/payment-types-store'
import {
  ApiCustomerTax,
  CustomerTaxesStore,
} from '../factories/database/customer-taxes-store'
import { ProductsStore } from '../factories/database/products-store'
import { SalesStore } from '../factories/database/sales-store'
import {
  PromotionPayloadLineItemParams,
  SaleTransformer,
} from '../factories/sale-transformer'
import {
  EVENT,
  VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE,
  DebounceOptions,
  DEBOUNCE_OPTIONS,
  FEATURE_ADD_ECOM_CUSTOM_CHARGES_ROW,
} from '../constants'
import { $injector, $log, $q, $rootScope, $window } from '../ng-compat/imports'
import { env } from '../../../lib/env'
import { wrToastSuccess } from '../components/react/helios/ToastNotifications/WrToastNotifications'
import {
  newCurrentSale,
  removeCustomer,
  ReduxCurrentSale,
  selectCurrentSale,
  selectLineItems,
  setCurrentSale,
  updateLineItem,
  setSyncStatus,
  saveComplete,
  localIdGenerated,
  setPromoCode,
  selectCurrentSaleAsSale,
  addLineItem,
  updateSaleDiscount,
  parkedOrQuoteTransitioned,
  updateSaleStatus,
  saleReadyToBeCompleted,
  updateCustomerBalances,
  accountBalancePaymentAdded,
  displayStatusChanged,
  dateChanged,
  registerChanged,
  userChanged,
  receiptNumberDetailsChanged,
  attachCustomerToSale,
  selectSaleCustomer,
  finaliseSaleSyncStatus,
  updatePickable,
  selectSalePromoCodeStatus,
  selectPromoCodeAndStatus,
  setCustomerBalances,
  addPayment,
  addTip,
  selectLineItem,
  removeLineItem,
  setPotentialPromotionId,
  setUserRemovedSaleServiceFee,
  selectSaleStatus,
  saleClosed,
  setChange,
  updateLineItems,
  selectSaleFinals,
  updateLineItemTax,
  addSurcharge,
  updatePayAccountBalanceWithPayment,
  setPromoCodeStatus,
  setPromoCodePromotionId,
  selectSalePayments,
  clearPotentialPromotionId,
  selectPotentialPromotionId,
  setReadyToComplete,
  removeFulfilmentState,
  addUnknownLineItem,
  removeEcomCustomCharge,
} from '../state/slices/currentSale/currentSaleSlice'
import { PromotionDetails } from '../models/promotion'
import {
  PromotionsData,
  PromotionsPayload,
  fetchPromotionsData,
} from '../components/sale/sale-promotion/sale-promotion-resource'
import { calculateTotalTaxRate } from '../sale-calculation/calculate-tax'
import {
  calculatePriceComponents,
  calculateTaxAppropriatePrice,
  calculateUnroundedDisplayDiscountInfo,
} from '../sale-calculation/calculate-price'
import { SaleTrackerService } from '../components/sale/sale-tracker/sale-tracker.service'
import { HotkeysService } from '../components/hotkeys/hotkeys.service'
import { SaleInventoryData } from '../components/sale/sale-inventory-data/sale-inventory-data.service'
import { isSameReceiptDetails } from '../util/receipt-compare'
import {
  postEmailReceipt,
  doUpdateSale,
  updateCustomFields,
} from '../functions/complete-sale'
import { EventEmitter } from '../util/event-emitter'
import { createSaleDiscount } from '../models/sale-discount'
import { containsGiftCard, isPriceDiscounted } from '../functions/lineItem'
import { FulfilmentType, hasDefinedFulfilmentType } from '../models/fulfilment'
import { entityCustomFieldsToMap } from '../functions/sale-transformer'
import { ReduxSaleStorageAdapter } from '../state/slices/currentSale/reduxSaleStorageAdapter'
import { getAdjustedAccountBalance } from '../functions/customer-balance'
import {
  calculatePaymentSurchargeForRefund,
  totalSurchargeFromPayments,
} from '../sale-calculation/calculate-sale'
import { hasServiceOrder } from '../functions/service'
import {
  hasCashDiscountingServiceFee,
  isABPAddedButNotPaid,
} from '../functions/payments'
import { SaleModals } from '../modals/sale-modals'
import { SaleLoader } from '../factories/sale-loader'
import type { BalancesResource } from '../factories/balance-resource'
import {
  openFulfilmentLineItemPopover,
  resetSaleCustomerContactInfoReviewStatus,
} from '../state/slices/uiStateSlice'
import { RegisterWorkflowsEvent } from '../components/workflows/workflow.service'
import { selectSaleReturnFor } from '../state/selectors'
import { PriceManager } from './price-manager'
import { RegisterManager } from './register-manager'
import { TaxManager } from './tax-manager'
import { UserManager } from './user-manager'
import { WebRegisterStatusManager } from './web-register-status-manager'
import { SyncManager } from './sync-manager'
import { PosStateManager } from './pos-state-manager'
import { SaleSyncManager } from './sale-sync-manager'
import { CashMovementsManager } from './cash-movements-manager'
import { PaymentManager, ReceiptTransaction } from './payment-manager'
import { clientApiEventBus } from './client-api-event-bus'
import { CustomerBalancesManager } from './customer-balances-manager'

const copy = angular.copy

export interface PaymentData {
  isSignatureSlipReceipt: boolean
  paymentType?: ApiRetailerPaymentType
  transaction?: ReceiptTransaction
}
export interface ReceiptData {
  trainingModeEnabled: boolean | undefined
  sale: Sale
  register: ApiRegister
  outlet: ApiOutlet
  receiptHtmlExtra: string | null
  paymentData?: PaymentData
  selectedReceiptId?: string
  giftCardsInfo?: {
    giftCardSettings: any
    giftCardPaymentsSummary: any
  }
}

type Events = {
  earlySubmitSaleComplete: []
  /** We already has sale:new event as angular - append EventEmitter to avoid confusion for now */
  'EventEmitter:sale:new': []
  [EVENT.SALE_ADD_LINE_ITEM]: [
    {
      lineItem: LineItem
      context?: {
        workflow?: RegisterWorkflowsEvent
      }
    }
  ]
  [EVENT.SALE_ADD_UNKNOWN_LINE_ITEM]: []
  'saleManager:saleCalculating:start': []
  'saleManager:saleCalculating:end': []
}

/**
 * Responsible for managing Sales
 */
// @todo reorder methods to separate public/private - this module is a bit of a mess / hard to navigate
export class SaleManager extends EventEmitter<Events> {
  public closeAfterComplete = false
  public isInitialised = false
  public queueSaleCalculation!: () => Promise<void | Sale>
  public saleCalculating = false
  public debounceOptions: DebounceOptions | undefined
  private hotkeysService!: HotkeysService
  private promotionsEnabled = false
  private whenInitialised?: IPromise<void | Sale>

  constructor(
    private readonly priceManager: PriceManager,
    private readonly posStateManager: PosStateManager,
    private readonly salesStore: SalesStore,
    private readonly saleSyncManager: SaleSyncManager,
    private readonly registerManager: RegisterManager,
    private readonly syncManager: SyncManager,
    private readonly taxManager: TaxManager,
    private readonly taxesStore: TaxesStore,
    private readonly vdTracking: VdTracking,
    private readonly saleLoader: SaleLoader,
    private readonly userManager: UserManager,
    private readonly cashMovementsManager: CashMovementsManager,
    wrDebugTools: any,
    private readonly customerBalancesManager: CustomerBalancesManager,
    private readonly webRegisterStatusManager: WebRegisterStatusManager,
    private readonly productsStore: ProductsStore,
    private readonly customerTaxesStore: CustomerTaxesStore,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly saleTransformer: SaleTransformer,
    private readonly saleModals: SaleModals,
    private readonly vdFeatureManager: VdFeatureManager,
    private readonly saleInventoryData: SaleInventoryData,
    private readonly paymentManager: PaymentManager,
    private readonly BalancesResource: BalancesResource,
    private readonly saleTracker: SaleTrackerService,
    private readonly $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'
    super()
    wrDebugTools.register('saleManager')
    this.debounceOptions = DEBOUNCE_OPTIONS
  }

  /**
   * Initialise the sale manager, loading any saved sale from the database.
   *
   * @return {Promise} A promise resolved once the sale manager is ready
   */
  public init() {
    if (!this.whenInitialised) {
      // Having to inject directly because of a circular reference dependency
      this.hotkeysService = $injector.get('hotkeysService')
      this.promotionsEnabled = this.vdFeatureManager.get('promotions')
      this.queueSaleCalculation = debouncePromise(
        this.saleCalculationQueue,
        this.getSaleCalculationDelay()
      )

      const whenRegisterOpen = this.registerManager
        .init()
        .then(() => this.registerManager.waitForOpenRegister())

      this.whenInitialised = $q
        .all({
          activeRegister: whenRegisterOpen,
          syncComplete: this.syncManager.whenCompleteDatasetAvailable(),
          priceManagerInitialised: this.priceManager.init(),
          taxManagerInitialised: this.taxManager.init(),
        })
        .then(() => this._init())
        .catch(error => {
          trackError('[SaleManager] Failed to initialise sale manager', {
            data: { error },
          })
          return $q.reject(error)
        })
        .finally(() => {
          this.measureStorage()
        })
    }

    return this.whenInitialised
  }

  private async measureStorage() {
    if (!this.vdFeatureManager.get('frontend_metrics')) {
      return
    }

    const storage = await getStorageEstimate()

    if (
      !storage ||
      storage.percentUsed === undefined ||
      storage.quotaMB === undefined
    ) {
      return
    }

    // Percentage used
    IncCounter('webregister.storage.used_size.percentage', 1, {
      percentage: this.getStoragePercentageTag(storage.percentUsed),
    })

    // Max storage
    IncCounter('webregister.storage.max', 1, {
      limit: this.getStorageMaxSpaceTag(storage.quotaMB),
    })
  }

  private getStoragePercentageTag(usedPercentage: number): string {
    const roundedPercentage = Math.ceil(usedPercentage / 10) * 10

    return `lte${roundedPercentage}%`
  }

  private getStorageMaxSpaceTag(maxSpace: number): string {
    const limits = [10, 50, 100, 200, 500, 1000, 10000, 100000]

    for (let i = 0; i < limits.length; i++) {
      if (maxSpace <= limits[i]) {
        return `lte${limits[i]}MB`
      }
    }

    return `gt${limits[limits.length - 1]}MB`
  }

  /**
   * Get the total saving for the current sale. Lineitem & Sale discounts
   *
   * @return {Number} Returns the total amount of savings for the current sale
   */
  public getTotalSavings() {
    let saleDiscount: string | number = 0
    let lineItemDiscountTotal = 0

    const currentSale = selectCurrentSaleAsSale(this.$ngRedux.getState())

    if (currentSale.discount) {
      if (this.registerManager.isTaxInclusive()) {
        saleDiscount = add(currentSale.discount.price, currentSale.discount.tax)
      } else {
        saleDiscount = currentSale.discount.price
      }
    }

    currentSale.lineItems.forEach(lineItem => {
      if (isPriceDiscounted(lineItem)) {
        if (this.registerManager.isTaxInclusive()) {
          lineItemDiscountTotal = add(
            lineItemDiscountTotal,
            subtract(
              lineItem.totals.inclusiveBasePrice,
              lineItem.totals.inclusivePrice
            )
          )
        } else {
          lineItemDiscountTotal = add(
            lineItemDiscountTotal,
            subtract(
              lineItem.totals.exclusiveBasePrice,
              lineItem.totals.exclusivePrice
            )
          )
        }
      }
    })

    return round(add(saleDiscount, lineItemDiscountTotal))
  }

  /**
   * Get promotions details of current sale.
   *
   * @return {Array} promotionsList: All related promotions list
   */
  public getPromotionSummary() {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    return getPromotionSummary(lineItems)
  }

  /**
   * Get lineItems product ids array
   *
   * @return All the product ids for current sale
   */
  public getLineItemProductIds(): string[] {
    const result: string[] = []
    const lineItems = selectLineItems(this.$ngRedux.getState())
    if (lineItems.length) {
      lineItems.forEach(item => {
        const id = item.product.id
        if (!result.includes(id)) {
          result.push(id)
        }
      })
    }
    return result
  }

  /**
   * Get total discount amount of all promotions
   *
   * @return {Number} The total discount amount for current sale
   */
  public getTotalPromotionAmount() {
    const currentPromotions = this.getPromotionSummary()

    return currentPromotions.reduce(
      (total, promo) => add(total, promo.total!),
      0
    )
  }

  /**
   * Get hasActivePromotions value from syncManager
   *
   * @return {Boolean} True if has active promotions, false otherwise
   */
  public getHasActivePromotions() {
    return this.syncManager.hasActivePromotions
  }

  /**
   * Set the given Sale as the active sale on the manager after recalculating prices, taxes, etc.
   *
   * @param  {Sale} sale
   *         The sale to set as the active sale
   *
   * @param  [saleModificationOptions] {Object}
   *
   *         @param [saleModificationOptions.unconfirmedSalesOnly=false] {Boolean}
   *                Only set unconfirmed (i.e. not Layby/On Account) sales as modified prior to saving
   */
  public setActiveSale(
    sale: Sale,
    saleModificationOptions?: {
      unconfirmedSalesOnly: boolean
    }
  ): Promise<Sale> {
    // Should not recalculate or look up inventory data when
    // - sale is closed
    // - sale is uploaded to be completed
    if (sale && (isClosed(sale.status) || isUploadedToBeCompleted(sale))) {
      this._setActiveSale(sale)
      return this.updateCustomerBalances().then(() =>
        this.saveSale(saleModificationOptions)
      )
    }

    return Promise.all(
      map(sale.lineItems, lineItem =>
        this.priceManager
          .updateLineItemPricesAndTaxes(lineItem, sale)
          .then(updatedLineItem => {
            sale.updateLineItem(updatedLineItem) // DO not use dispatch here because at this stage sale is not in redux yet.
          })
      )
    )
      .then(() => {
        let resolver = Promise.resolve()
        this._setActiveSale(sale)

        sale.lineItems.forEach(lineItem => {
          resolver = resolver.finally(() =>
            this.saleInventoryData.lookupInventoryData(lineItem)
          )
        })

        return resolver
      })
      .then(() => this.priceManager.totalSale(sale, false))
      .then(() => this.updateCustomerBalances())
      .then(() => this.saveSale(saleModificationOptions))
  }

  /**
   * Save the managed sale to IndexedDB and register it as the active sale in the POS state manager.
   *
   * @param  [saleModificationOptions] {Object}
   *
   *         @param [saleModificationOptions.unconfirmedSalesOnly=false] {Boolean}
   *                Only set unconfirmed (i.e. not Layby/On Account) sales as modified prior to saving
   *
   * @return {Promise<Sale>} A promise resolved with the sale once it has been saved to IndexedDB
   */
  public saveSale(saleModificationOptions?: {
    unconfirmedSalesOnly: boolean
  }): Promise<Sale> {
    this.setSaleModified(saleModificationOptions)
    return this._saveSale().then(saleModel => {
      clientApiEventBus.emitEvent({
        eventName: clientApiEventBus.EVENTS.SALE_UPDATE,
        data: {
          sale: this.saleTransformer.toClientApi(
            saleModel,
            saleModel.lineItems
          ),
        },
      })

      return saleModel
    })
  }

  /**
   * Determines if the retailer should be prompted for a course of action in dealing with the current sale, prior
   * to loading a new sale.
   *
   * @param options {Object}
   *
   *        @param [saleToLoad] {Sale|String} The requested sale to load.
   *        @param [context] {String} The context the action is occuring; provides contextual content for the modal
   *                                  instance.
   *
   * @return {Promise<Object>} Resolved with the current hasActiveSale status, once a course of action has
   *                           been chosen.
   */
  public actionExistingSaleIfRequired({
    saleToLoad,
    context,
  }: {
    saleToLoad?:
      | string
      | {
          id: string
        }
      | {
          localId: string
        }
    context?: string
  }): IPromise<{
    hasActiveSale: boolean
  }> {
    const saleIdToLoad =
      saleToLoad &&
      ((saleToLoad as any).id || (saleToLoad as any).localId || saleToLoad)
    const existingSale = selectCurrentSale(this.$ngRedux.getState())

    if (
      !existingSale ||
      (saleIdToLoad &&
        (saleIdToLoad === existingSale.id ||
          saleIdToLoad === existingSale.localId))
    ) {
      return $q.resolve({ hasActiveSale: this.hasActiveSale() })
    }

    return this.getRequiredExistingSaleAction(context).then(() => ({
      hasActiveSale: this.hasActiveSale(),
    }))
  }

  /**
   * Determine the appropriate action when attempting to load a new sale, but an existing sale is currently in play.
   *
   * @param {String} context
   *         The context the action is occuring in; provides contextual content for the modal instance.
   *
   * @return {Promise} Resolved once the appropriate action has been chosen.
   */
  private getRequiredExistingSaleAction(context?: string) {
    const existingSale = selectCurrentSale(this.$ngRedux.getState())
    const lineItems = selectLineItems(this.$ngRedux.getState())
    if (isConfirmed(existingSale) || existingSale.isSavedUnfulfilled) {
      return this.showActionConfirmSaleAndFinalise(context)
    }

    if (hasPayments(existingSale)) {
      return this.saleModals.actionConfirmSaleWithPayments()
    }

    if (containsGiftCard(lineItems)) {
      return this.saleModals.actionConfirmSaleWithGiftCard()
    }

    if (hasLineItems(existingSale)) {
      return this.showActionConfirmSaleAndFinalise(context)
    } else {
      // Clear the sale since it's not a Confirmed sale; it has no payments, no gift cards and no line items
      return this.clearSale()
    }
  }

  /**
   *  Shows the confirm sale modal, passing _saveAndFinaliseCurrentSale as the save sale resolver.
   *
   * @param {String} context
   *         The context the action is occuring in; provides contextual content for the modal instance.
   *
   * @return {Promise} A promise resolved once a course of action has been chosen from the modal.
   */
  private showActionConfirmSaleAndFinalise(context?: string) {
    const sale = selectCurrentSale(this.$ngRedux.getState())
    return this.saleModals.actionConfirmSale({
      saveSaleResolver: () => this.saveAndFinaliseCurrentSale(),
      context,
      isAccountBalancePaymentAdded: isAccountBalancePaymentAdded(
        sale.payAccountBalance
      ),
    })
  }

  /**
   * Add product to sale
   *
   * @param product {Object}
   *          The product item to be priced & added to the sale.
   *
   * @param options {Object}
   *        @param [options.price] {String}
   *               The price to set on the inventory item.
   *
   *        @param [params.quantity] {String|Number}
   *               The quantity to set on the inventory item.
   *
   *        @param [params.confirmed=false] {Boolean}
   *               Whether the item should be added confirmed or not.
   *
   *        @param [options.note] {String}
   *               A note to set on the newly created line item
   *
   * @param context {Object}
   *          Not used internally, but included in the sale:add-line-item event.
   *
   * @return {Promise<LineItem>} A promise resolved with the added LineItem once the sale has been totalled and
   *      saved to IndexedDB
   */
  public addProductToSale(
    product: ApiProduct,
    options: {
      price?: any
      quantity?: string | number
      confirmed?: boolean
      note?: string
      user?: LineItemUser
    } = {},
    context?: { workflow?: RegisterWorkflowsEvent }
  ): IPromise<LineItem> {
    const price = options.price
    const quantity = isFinite(options.quantity) ? options.quantity : '1'
    const confirmed = options.confirmed || false
    const note = options.note
    const adjusted = isFinite(price)
    const user = options.user || null

    let lineItemConfig = {
      price,
      quantity,
      confirmed,
      adjusted,
      product,
      salesperson: user,
    } as LineItemInput

    // Setting custom note on creation if passed.
    if (note) {
      lineItemConfig.note = note
    }

    // Set fulfilment type from sale.fulfillmentDetails if it exists
    lineItemConfig = this.setInitialFulfillmentType(lineItemConfig)

    const lineItem = this.addLineItem(lineItemConfig)

    const { getState, dispatch } = this.$ngRedux
    const currentSale = selectCurrentSaleAsSale(getState())

    // Update the price of the line item (and all related line items)
    return this.priceManager
      .updateLineItemPricesAndTaxes(lineItem, currentSale)
      .then(updatedLineItem => dispatch(updateLineItem(updatedLineItem)))
      .then(() =>
        this.evaluateNewLineItemDisplayPrice(
          this.getLineItemFromSale(lineItem),
          price
        )
      )
      .then(() =>
        this.saleInventoryData.lookupInventoryData(
          this.getLineItemFromSale(lineItem)
        )
      )
      .then(() => this.queueSaleCalculation())
      .then(() =>
        this.emit(EVENT.SALE_ADD_LINE_ITEM, {
          lineItem: this.getLineItemFromSale(lineItem),
          context,
        })
      )
      .then(() => this.getLineItemFromSale(lineItem))
      .then(() => {
        // TODO: Investigate id on acceptance of inventory issue affecting multiple identical products
        // https://lightspeedhq.atlassian.net/browse/LRX-16816?focusedCommentId=1585200
        const isFulfillmentLifB3FFEnabled =
          this.vdFeatureManager.get('fulfillment_lif') &&
          this.vdFeatureManager.get('fulfillment_lif_b3')
        const reduxCurrentSale = selectCurrentSale(this.$ngRedux.getState())
        const isReferencedReturn = !!selectSaleReturnFor(
          this.$ngRedux.getState()
        )
        if (
          isFulfillmentLifB3FFEnabled &&
          this.saleInventoryData.shouldValidateInventory(lineItem) &&
          !this.saleInventoryData.hasInventory(lineItem) &&
          lineItem.fulfilmentType === FulfilmentType.UNSPECIFIED &&
          !hasFulfilmentsCompleted(currentSale.lineItems) &&
          !isNonFulfillmentOnAccountSale(reduxCurrentSale) &&
          !isReferencedReturn
        ) {
          this.$ngRedux.dispatch(
            openFulfilmentLineItemPopover(lineItem.localId)
          )
        }
        return lineItem
      })
  }

  public addUnknownProductToSale(name: string) {
    this.$ngRedux.dispatch(addUnknownLineItem(name))
    return this.saveSale().then(() => {
      this.emit(EVENT.SALE_ADD_UNKNOWN_LINE_ITEM)
    })
  }

  public setInitialFulfillmentType(
    lineItemConfig: LineItemInput
  ): LineItemInput {
    const currentSale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    if (!hasFulfilmentDetails(currentSale)) {
      return lineItemConfig
    }

    // Any new line items added should be take now in the following cases:
    // - Sale has fulfilment completed
    // - We are not continuing an unfulfilled sale
    if (
      hasFulfilmentsCompleted(currentSale.lineItems) ||
      currentSale.isNotSavedUnfulfilled()
    ) {
      return {
        ...lineItemConfig,
        fulfilmentType: FulfilmentType.UNSPECIFIED,
      }
    }

    return {
      ...lineItemConfig,
      fulfilmentType: currentSale.fulfilmentDetails[0].type,
    }
  }

  /**
   * Finds and returns the passed line item in a sale
   * To help with the line items migration to Redux, we have channeled all line
   * item operations through the sale model which updates the sale in
   * SaleManager. This function helps to get the line item from the sale which
   * has the latest information
   *
   * @param lineItem
   * @returns LineItem
   */
  private getLineItemFromSale(lineItem: LineItem): LineItem {
    const selectedLineItem = selectLineItem(lineItem.localId)(
      this.$ngRedux.getState()
    )
    return selectedLineItem ?? lineItem
  }

  /**
   * Add special gift card item to sale
   *
   * @param options.product {Object}
   *          The product to be priced & added to the sale.
   *
   * @param options.number {string}
   *          The gift card number
   *
   * @param options.balance {number}
   *          The gift card balance
   *
   * @param [options.expiresAt] {Date}
   *          The UTC of when the gift card will expire
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totalled and
   *      saved to IndexedDB
   */
  public addGiftCardItem({
    product,
    code,
    balance,
    expiresAt,
    isReload,
    clientId,
  }: {
    product: ApiProduct
    code: string
    balance: number | string
    expiresAt: string | null
    isReload: boolean
    clientId: string
  }) {
    const lineItem = this.addLineItem({
      product,
      basePrice: balance.toString(),
      giftCardNumber: code,
      giftCardReload: isReload,
      giftCardClientId: clientId,
      giftCardExpiresAt: expiresAt ?? undefined,
      registerId: this.registerManager.getActiveRegister().id,
      registerSequenceId:
        this.registerManager.getActiveRegister().register_open_sequence_id ||
        '',
      displayPrice: '', // These fields are default to empty but eventually will be updated in the call back function bellow
      displayDiscount: '', // In case they are not, we are going to have a lot of issue
      displayDiscountPercent: '',
      displayMarkupPercent: '',
      displayBasePrice: '',
      displayBestDiscountPercent: '',
      displayBestDiscountPrice: '',
    })
    // Update the price of the line item (and all related line items)
    return this.priceManager
      .updateLineItemPricesAndTaxes(
        lineItem,
        selectCurrentSaleAsSale(this.$ngRedux.getState())
      )
      .then(updatedLineItem =>
        this.$ngRedux.dispatch(updateLineItem(updatedLineItem))
      )
      .then(() => {
        this.emit(EVENT.SALE_ADD_LINE_ITEM, {
          lineItem: this.getLineItemFromSale(lineItem),
        })
        return this.queueSaleCalculation()
      })
  }

  /**
   * Add the given customer to the Sale.
   *
   * @param  customer {Customer}
   *         The customer to add to the sale
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once prices etc have been recalculated
   */
  public async addCustomer(customer: ApiCustomer): Promise<Sale> {
    // Should remove customer first before adding, to avoid the scenario:
    // - customer A (with tax setting) already applied to sale
    // - need to add customer B (without tax setting) to sale
    // - if don't remove A first, after calling addCustomer(B), customer tax of A will still apply to sale
    // (Currently, this scenario only happens on unfulfill/service modal when changing customer)
    if (selectSaleCustomer(this.$ngRedux.getState<RootState>())) {
      await this.removeCustomer()
    }
    this.$ngRedux.dispatch(attachCustomerToSale(customer))

    return this.updateCustomerBalances()
      .then(() => $rootScope.$emit(EVENT.SALE_CUSTOMER_UPDATED))
      .then(() => this.recalculateCustomerTax(customer.id))
      .then(() => this.queueSaleCalculation())
      .then(() => selectCurrentSaleAsSale(this.$ngRedux.getState()))
  }

  /**
   * Used to update the customer balances on the sale
   *
   * @todo add check for valid customer
   *
   * @param  {Customer} customer
   *         The customer which is to be evaluated
   *
   * @return {Promise} A promise resolved with the updated customer balances
   */
  public updateCustomerBalances(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const { getState, dispatch } = this.$ngRedux
      const saleCustomer = selectSaleCustomer(getState())
      if (isValidCustomer(saleCustomer)) {
        this.customerBalancesManager
          .getCustomerBalances(saleCustomer)
          .then((result: CustomerBalances) => {
            dispatch(setCustomerBalances(result))
            return result
          })
          .then(resolve, reject)
      } else {
        dispatch(setCustomerBalances(null))
        resolve(null)
      }
    })
  }

  /**
   * Remove the currently assigned customer from the Sale.
   *
   * @return {Promise<Sale|void>} Resolved with the updated Sale, or undefined if the sale is considered empty.
   */
  public removeCustomer() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState<RootState>())
    const customerId = currentSale?.customer?.id

    this.$ngRedux.dispatch(removeCustomer())

    if (currentSale.payAccountBalance) {
      $rootScope.$emit(EVENT.SALE_UPDATE_TENDERED_AMOUNT)
    }

    $rootScope.$emit(EVENT.SALE_CUSTOMER_UPDATED)

    // Clear the sale if it's a parked sale and now empty after removing.
    if (isEmptyParkedSale(currentSale)) {
      return this.clearSale()
    }

    if (isEmptySale(currentSale)) {
      return this.saveSale()
    }

    return this.recalculateCustomerTax(customerId).then(() =>
      this.queueSaleCalculation()
    )
  }

  /**
   * Recalculate lineItem tax when add/remove customer
   *
   * @return A promise resolved with the updated sale once customer tax has been applied
   */
  public recalculateCustomerTax(customerId?: string) {
    if (!customerId || isClosed(selectSaleStatus(this.$ngRedux.getState()))) {
      return Promise.resolve()
    }

    /**
     * typescript still infer as IPromise given returnNativePromise flag is true
     * need to cast to Promise<ApiCustomerTax | undefined>
     */
    return (this.customerTaxesStore.get(customerId, true) as Promise<
      ApiCustomerTax | undefined
    >).then(data => {
      if (data) {
        return this.priceManager.recalculateSale(
          selectCurrentSaleAsSale(this.$ngRedux.getState()),
          {
            isUpdatingCustomer: true,
          }
        )
      }
    })
  }

  /**
   * Add a new line item to the sale based on the given line-item-config.
   *
   * @return {LineItem} The created line item.
   */
  private addLineItem(lineItemConfig: LineItemInput): LineItem {
    // Create Line Item
    const lineItem = createLineItem({
      ...lineItemConfig,
      registerId: this.registerManager.getActiveRegister().id,
      registerSequenceId: this.registerManager.getActiveRegister()
        .register_open_sequence_id!,
    })

    // Add to Sale
    this.$ngRedux.dispatch(addLineItem(lineItem))

    return lineItem
  }

  /**
   * Total the Sale (total line items and then total the sale).
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totalled and
   *      saved to IndexedDB
   */
  public totalSale(): Promise<Sale> {
    this.priceManager.totalSale(
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )
    return this.saveSale()
  }

  /**
   * Remove given line item from sale.
   *
   * @param lineItem {LineItem}
   *        The LineItem to delete from the Sale.
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totalled and
   *         saved to IndexedDB.
   */
  public removeLineItem(lineItem: LineItem): Promise<void | Sale> {
    const { getState, dispatch } = this.$ngRedux
    let lineItems = selectLineItems(getState())
    const currentSale = selectCurrentSale(getState())

    this.saleInventoryData.addRemoveQuantity(lineItem, {
      onRemove: true,
      hasLineItems: Boolean(lineItems.length),
    })

    dispatch(removeLineItem(lineItem))

    // Trigger update_validation event again after removing
    this.saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
    // save the fulfillment type for use later
    lineItems = selectLineItems(getState())
    const lineItemsHaveFulfillmentType = hasDefinedFulfilmentType(lineItems)

    // If no items are fulfilment, clear the sale status and fulfillment details
    if (this.isLifBugFixesEnabled() && !lineItemsHaveFulfillmentType) {
      dispatch(removeFulfilmentState())
      if (isUnfulfilled(currentSale.status)) {
        dispatch(updateSaleStatus(undefined))
      }
    }

    // If there are still line items, perform a sale calculation
    if (lineItems.length) {
      return this.queueSaleCalculation()
    }

    // No line items, so reset sale inventory data and potentialPromotionId.
    this.saleInventoryData.reset()
    dispatch(setPotentialPromotionId(null))

    // Clear the sale if it's a parked sale and now empty after removing.
    if (isEmptyParkedSale(currentSale)) {
      return this.clearSale()
    }

    // Void the sale if it's a return. @todo we need to establish if all sales should be cleared / voided.
    if (isReturn(currentSale)) {
      return this.voidSale()
    }

    // Clear totals to represent line items have been removed.
    return this.totalSale()
  }

  /**
   * The status of the Line Item Fulfillment bug fixes feature flag.
   * @returns boolean
   */
  private isLifBugFixesEnabled() {
    return (
      this.vdFeatureManager.get('fulfillment_lif') &&
      this.vdFeatureManager.get('fulfillment_lif_b3') &&
      this.vdFeatureManager.get('fulfillment_lif_bug_fixes')
    )
  }

  /**
   * Update the sale-level discount on the current sale.
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totaled and
   *      saved to IndexedDB
   */
  public updateSaleDiscount() {
    const { getState, dispatch } = this.$ngRedux
    const getSaleDiscount = () => selectCurrentSale(getState()).discount

    const initialDiscount = copy(getSaleDiscount()) || null

    // total current sale
    this.priceManager.totalSale(selectCurrentSaleAsSale(getState()))

    // priceManager.totalSale mutate sale.discount so need to get discount from redux again
    const saleDiscount = getSaleDiscount()
    if (saleDiscount) {
      const {
        id,
        register_open_sequence_id,
      } = this.registerManager.getActiveRegister()
      const newSaleDiscount = createSaleDiscount({
        ...saleDiscount,
        registerId: id,
        registerSequenceId: register_open_sequence_id!,
      })
      dispatch(updateSaleDiscount({ discount: newSaleDiscount }))
    }
    // if dispatched, sale discount mutated, so need to get discount from redux again
    if (!isEqual(getSaleDiscount(), initialDiscount)) {
      return this.saveSale()
    }

    // if saveSale called, sale mutated so need to get sale from redux again
    const currentSale = selectCurrentSaleAsSale(getState())
    return Promise.resolve(currentSale)
  }

  /**
   * For cash discounting; permanently removes the service fee from the sale.
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the service fee has been removed and
   *         saved to IndexedDB.
   */
  public removeServiceFeeFromSale(): Promise<Sale> {
    this.$ngRedux.dispatch(setUserRemovedSaleServiceFee(true))
    this.priceManager.totalSale(
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )

    return this.saveSale()
  }

  /**
   * Update the quantity of a given line item.
   *
   * @param details {Object}
   *
   *      @param details.lineItem {LineItem}
   *             The line item to update the quantity of
   *
   *      @param details.beforeQuantity
   *             The quantity of the line item before the update
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totaled and
   *      saved to IndexedDB
   */
  public updateLineItemQuantity(details: {
    lineItem: LineItem
  }): IPromise<void | Sale> {
    this.$ngRedux.dispatch(
      updateLineItem({
        ...details.lineItem,
        quantity: details.lineItem.displayQuantity,
      })
    )

    // Ensure any pricebook updates are applied & re-total the sale
    return this.saleInventoryData
      .reEvaluateQuantity(selectLineItems(this.$ngRedux.getState<RootState>()))
      .then(() => this.queueSaleCalculation())
  }

  /**
   * Update the display price of a given line item.
   *
   * @param details {Object}
   *
   *      @param details.lineItem {LineItem}
   *             The line item to update the display price of
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totalled and
   *      saved to IndexedDB
   */
  public updateLineItemDisplayPrice({ lineItem }: { lineItem: LineItem }) {
    const newDisplayPrice = lineItem.displayPrice
    const adjustedLineItem = this.getAdjustedLineItemPrice(
      newDisplayPrice,
      lineItem
    )

    const displayDiscountInfo = calculateUnroundedDisplayDiscountInfo(
      adjustedLineItem.basePrice,
      adjustedLineItem.baseTax,
      adjustedLineItem.price,
      adjustedLineItem.tax,
      this.registerManager.isTaxInclusive()
    )

    this.$ngRedux.dispatch(
      updateLineItem({
        ...adjustedLineItem,
        displayDiscountPercent: displayDiscountInfo.discountPercent,
        displayMarkupPercent: displayDiscountInfo.markupPercent,
        displayDiscount: displayDiscountInfo.discount,
      })
    )

    return this.queueSaleCalculation()
  }

  /**
   * Update the price by a given discount percent for the given line item.
   *
   * @param details {Object}
   *
   *      @param details.lineItem {LineItem}
   *             The line item to update the percent discount for
   *
   *      @param details.beforeDiscountPercent
   *             The display discount percent of the line item before the update
   *
   * @return {Promise<Sale>} A promise resolved with the updated Sale once the sale has been totaled and
   *      saved to IndexedDB
   */
  public updateLineItemDiscountPercent(details: { lineItem: LineItem }) {
    const lineItem = details.lineItem
    const basePriceTaxAppropriate = calculateTaxAppropriatePrice(
      lineItem.basePrice,
      lineItem.baseTax,
      this.registerManager.isTaxInclusive()
    )
    const displayBasePrice = parseFloat(round(basePriceTaxAppropriate, 5))
    const discountDecimal = VendNumber.divide(
      lineItem.displayDiscountPercent,
      100
    )

    // Work out discount from base price
    const discountValue = VendNumber.multiply(discountDecimal, displayBasePrice)
    const discountedPrice = VendNumber.subtract(displayBasePrice, discountValue)
    const adjustedLineItem = this.getAdjustedLineItemPrice(
      discountedPrice,
      lineItem
    )

    this.$ngRedux.dispatch(
      updateLineItem({
        ...adjustedLineItem,
        displayPrice: VendNumber.round(discountedPrice, 5),
        displayMarkupPercent: VendNumber.subtract(
          0,
          adjustedLineItem.displayDiscountPercent
        ),
        displayDiscount: VendNumber.round(discountValue, 5),
      })
    )

    return this.queueSaleCalculation()
  }

  /**
   * Initiate the sale calculation queue
   *
   * @return {Promise} A promise resolved with sale calculations (promotions & totals)
   */
  public saleCalculationQueue(): Promise<void | Sale> {
    const sale = selectCurrentSale(this.$ngRedux.getState<RootState>())
    if (
      isEmptySale(sale) ||
      isCompleted(sale.status) ||
      isConfirmedReadyToComplete(sale)
    ) {
      return Promise.resolve()
    }

    this.saleCalculating = true
    this.emit('saleManager:saleCalculating:start')

    let timer: Timer | undefined
    if (this.vdFeatureManager.get('frontend_metrics')) {
      timer = StartTimer('webregister.sale_calculation_timer', {}, 10)
    }

    return this.checkPromotionsAndUpdateLineItemsPricing()
      .then(() => this.totalSale())
      .finally(() => {
        this.saleCalculating = false
        this.emit('saleManager:saleCalculating:end')

        if (timer) {
          timer.stop()
        }
      })
  }

  public hasCashDiscountingServiceFee() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState())
    return hasCashDiscountingServiceFee(
      this.registerManager.hasCashDiscounting(),
      currentSale
    )
  }

  /**
   * Get the outstanding sale balance for when the outlet is using cash discounting.
   */
  public getCashDiscountedOutstandingBalance() {
    if (!this.hasCashDiscountingServiceFee()) {
      return
    }
    const sale = selectCurrentSale(this.$ngRedux.getState())
    const outstandingBalance = getOutstandingBalance(sale)
    const totalDistributedFee = add(
      sale.totals.saleServiceFeePaid,
      vn(sale.totals.cashDiscount).abs()
    )
    const remainingServiceFee = subtract(
      sale.totals.saleServiceFee,
      totalDistributedFee
    )

    return round(subtract(outstandingBalance, remainingServiceFee))
  }

  /**
   * Check if current sale can be marked as unfulfilled.
   *
   * @return {Boolean} True if the the sale can be marked as unfulfilled, false otherwise.
   */
  public canMarkAsUnfulfilled() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState())

    if (!currentSale) {
      return false
    }

    return Boolean(
      hasLineItems(currentSale) &&
        !hasUnknownLineItems(currentSale) &&
        !isReturn(currentSale) &&
        !isBlindReturn(currentSale) &&
        !isOnAccount(currentSale.status) &&
        !isLayby(currentSale.status)
    )
  }

  /**
   * Removes promotion data from all lineItems and re-evaluate prices.
   * !!Note!!: Pricebook relies on this func to update prices of existing lineItems, make any change
   *           in this func should also test through pricebook with MIN/MAX quantity setting.
   *
   * @return {Promise<Sale>} Updated Sale
   */
  private updateLineItemsPricingWithoutPromotions(): IPromise<void[]> {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    return Promise.all(
      lineItems.map(lineItem => {
        if (
          lineItem.taxInfo &&
          Boolean(
            lineItem.giftCardNumber || lineItem.isReturn || lineItem.confirmed
          )
        ) {
          return Promise.resolve()
        }

        return this.priceManager
          .updateLineItemPricesAndTaxes(
            lineItem,
            selectCurrentSaleAsSale(this.$ngRedux.getState()),
            {
              removePromotion: true,
            }
          )
          .then(updatedLineItem => {
            this.$ngRedux.dispatch(updateLineItem(updatedLineItem))
          })
      })
    )
  }

  /**
   * Evaluate and update the new line item display price if required
   *
   * @param {Object} customLineItemPrice
   *
   * @return {Promise} Returns a promise resolved with with the updated line item
   */
  private evaluateNewLineItemDisplayPrice(
    lineItem: LineItem,
    price: string | number
  ) {
    // For an inclusive retailer, if a custom price was set, the display price will have the tax calculated and
    // added ontop of the item price. However we want the passed price to be the display price regardless of
    // inclusive or exclusive stores. So once the line item has been created, we explicitly reset the display
    // price to the passed custom price before totalling the sale.
    // This case is most likely when adding an item via the client api with a different price set
    if (
      isFinite(price) &&
      this.registerManager.isTaxInclusive() &&
      lineItem.displayPrice !== price
    ) {
      return this.updateLineItemDisplayPrice({
        lineItem: {
          ...lineItem,
          displayPrice: price,
        },
      })
    }

    return $q.resolve()
  }

  /**
   * Apply a promo code to current sale, when param code equals to '' will remove any promo code from sale
   *
   * @param {String} code
   *        The promo code needs to be applied to the sale
   *
   * @return {Promise<String>}
   *          A promise resolved with validation status once the promo code has been applied to the sale
   */
  public addOrRemovePromoCode(code: string): Promise<string | null> {
    this.$ngRedux.dispatch(setPromoCode(code))

    return this.queueSaleCalculation().then(() => {
      return selectSalePromoCodeStatus(this.$ngRedux.getState())
    })
  }

  /**
   * Determine whether current sale has applied promo code
   *
   * @return {Boolean}
   *          True if current sale has applied promo code, false otherwise
   */
  public hasAppliedPromoCode(): boolean {
    const { promoCode, promoCodeStatus } = selectPromoCodeAndStatus(
      this.$ngRedux.getState()
    )
    if (promoCode && promoCodeStatus === PROMO_CODE_STATUES.APPLIED) {
      return true
    }

    // For continued Layby and OnAccount sale, when refresh the sell page, sale.promoCode will be empty,
    // and sale.promoCodeStatus will be 'other', need to double check promotion details
    const promotions = this.getPromotionSummary()

    return some(promotions, promotion => promotion.promo_code)
  }

  /**
   * Sends a snap shot of the given sale to the `api/2.0/discount` endpoint to check for any applicable and/or
   * potential promotions.
   *
   * @returns {Promise<PromotionsData | null>} A promise resolved with the applicable/potential promotions for the given
   *          sale or resolved with null if there are no relevant promotions to apply.
   */
  private fetchPromotionsDataForSale(): Promise<PromotionsData | null> {
    if (!this.promotionsEnabled || !this.syncManager.hasActivePromotions) {
      return Promise.resolve(null)
    }

    return this.toPromotionsPayload()
      .then(promotionsPayload => fetchPromotionsData(promotionsPayload))
      .then(response => response?.data || null)
  }

  private toPromotionsPayload(): Promise<PromotionsPayload> {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    return Promise.all(
      lineItems.map(lineItem => {
        return this.priceManager
          .getLineItemPricingWithoutPromotions(
            lineItem,
            selectCurrentSaleAsSale(this.$ngRedux.getState()) // Get the current sale so that we don't get stale sale data before this promise resolves
          )
          .then(lineItemPricing => ({
            ...lineItemPricing,
            localId: lineItem.localId,
            product: lineItem.product,
            quantity: lineItem.quantity,
            adjusted: lineItem.adjusted,
            confirmed: lineItem.confirmed,
          }))
      })
    ).then((lineItemPromotionParams: PromotionPayloadLineItemParams[]) => {
      const sale = selectCurrentSaleAsSale(this.$ngRedux.getState()) // Get the current sale so that we don't get stale sale data before this promise resolves
      return this.saleTransformer.toPromotionsPayload(
        sale.promoCode,
        sale.customer,
        lineItemPromotionParams
      )
    })
  }

  /**
   * Check if the current sale qualifies for a promotion, and if so apply it. Either way will update line item
   * pricing.
   *
   * @return {Promise} A promise resolved once the applicable promotions have been applied to the sale
   */
  private checkPromotionsAndUpdateLineItemsPricing(): Promise<void> {
    // Maintain current state while offline
    if (!this.webRegisterStatusManager.isOnline()) {
      return Promise.resolve()
    }

    return this.fetchPromotionsDataForSale()
      .then(promotionsData => {
        if (!promotionsData) {
          return this.clearPromotionsAndUpdateLineItemsPricing()
        }

        this.$ngRedux.dispatch(
          setPotentialPromotionId(
            promotionsData.potential_promotion?.id ?? null
          )
        )

        const potentialPromotionId = selectPotentialPromotionId(
          this.$ngRedux.getState()
        )
        if (potentialPromotionId) {
          // Store the potential promotion ID in the POS State so we know we've shown the promotion suggestion at
          // least once during the course of this sale, for UBE tracking purposes when the sale is completed.
          this.posStateManager.setPromotionSuggestion(potentialPromotionId)
        }

        if (
          promotionsData.promo_code_validation ||
          promotionsData.promotions?.length
        ) {
          return this.applyPromotionsAndUpdateLineItemsPricing(promotionsData)
        } else {
          return this.updateLineItemsPricingWithoutPromotions()
        }
      })
      .catch(error => {
        // Fallback to standard pricing
        $log.error('[saleManager] Failed to evaluate promotions', error)
        return this.clearPromotionsAndUpdateLineItemsPricing()
      })
  }

  /**
   * Clear any promotion details from the current sale and recalculate the line item pricing based on pricebooks only.
   *
   * @return {Promise<Sale>} A promise resolved once all promotion details have been removed and the line item prices
   *         recalculated.
   */
  private clearPromotionsAndUpdateLineItemsPricing() {
    // Clear any potential promotions
    this.$ngRedux.dispatch(clearPotentialPromotionId())
    return this.updateLineItemsPricingWithoutPromotions()
  }

  /**
   * Apply a given promotion to the sale
   *
   * @param {Object} promotionData
   *        The promotion data received from the promotions service containing sale and line item level promotion detail
   *
   * @return {Promise} A promise resolved with the current sale updated with the applied promotion
   */
  private applyPromotionsAndUpdateLineItemsPricing(
    promotionData: PromotionsData
  ): Promise<any> {
    const isTaxInclusive = this.registerManager.isTaxInclusive()

    const promoValidation = promotionData.promo_code_validation
    // When promotionData has promo_code_validation, means trying to apply a promo code to current sale
    if (promoValidation) {
      this.$ngRedux.dispatch(setPromoCodeStatus(promoValidation.status))
      if (promoValidation.status !== PROMO_CODE_STATUES.APPLIED) {
        // The current checking promo code is invalid
        this.$ngRedux.dispatch(setPromoCode(''))
      }
      this.$ngRedux.dispatch(
        setPromoCodePromotionId(promoValidation.promotion_id)
      )
    } else {
      this.$ngRedux.dispatch(setPromoCodeStatus(null))
    }

    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())

    return Promise.all(
      sale.lineItems.map(lineItem =>
        this.applyPromotionsAndUpdateLineItemPricing(
          promotionData,
          lineItem,
          isTaxInclusive
        )
      )
    )
  }

  /**
   * Apply the given promotions data to the given line item. If there is an applicable promotion it is applied to the
   * line item. If the line item promotion is not applicable it is removed. If the line item has no promotion it is recalculated.
   * This does not apply to confirmed or return line items.
   */
  private async applyPromotionsAndUpdateLineItemPricing(
    promotionData: PromotionsData,
    lineItem: LineItem,
    isTaxInclusive: boolean
  ): Promise<void> {
    if (lineItem.confirmed || lineItem.isReturn) {
      return
    }

    const promoValidation = promotionData.promo_code_validation
    const lineItemPromotion = find(
      promotionData.sale.line_items,
      lineItemPromotion => lineItemPromotion.id === lineItem.localId
    )

    if (lineItemPromotion?.promotion_ids?.length) {
      // Add promotions detail to the line item
      // Required for things like promotion name
      const promotionDetails: PromotionDetails[] = lineItemPromotion.promotion_ids.map(
        (promotionId: string) => {
          const data = find(promotionData.promotions, {
            id: promotionId,
          })!
          const shouldAddPromoCode =
            promoValidation?.promotion_id === promotionId &&
            promoValidation?.status === PROMO_CODE_STATUES.APPLIED

          const amount = calculateTaxAppropriatePrice(
            lineItemPromotion.unit_discount,
            lineItemPromotion.unit_tax_discount,
            isTaxInclusive
          )

          return {
            id: data.id,
            name: data.name,
            description: data.description,
            promoCode: shouldAddPromoCode ? promoValidation.promo_code : null,
            promoCodeId: shouldAddPromoCode
              ? promoValidation.promo_code_id
              : null,
            amount: vn(amount).toNumber(),
          }
        }
      )

      return this.priceManager
        .updateLineItemPricesAndTaxes(
          lineItem,
          selectCurrentSaleAsSale(this.$ngRedux.getState()),
          {
            lineItemPromotion,
            promotionDetails,
          }
        )
        .then(updatedLineItem => {
          this.$ngRedux.dispatch(updateLineItem(updatedLineItem))
        })
    }

    if (lineItem.lineItemPromotion) {
      // Remove any existing promotions that are no longer applicable
      return this.priceManager
        .updateLineItemPricesAndTaxes(
          lineItem,
          selectCurrentSaleAsSale(this.$ngRedux.getState()),
          {
            removePromotion: true,
          }
        )
        .then(updatedLineItem => {
          this.$ngRedux.dispatch(updateLineItem(updatedLineItem))
        })
    }

    // Line item with no promotions should also be updated, as a different pricebook may be applied
    return this.priceManager
      .updateLineItemPricesAndTaxes(
        lineItem,
        selectCurrentSaleAsSale(this.$ngRedux.getState())
      )
      .then(updatedLineItem => {
        this.$ngRedux.dispatch(updateLineItem(updatedLineItem))
      })
  }

  /**
   * Get the adjusted price to the line item.
   *
   * @param adjustedPrice {String}
   *        The adjusted price to apply.
   *
   * @param lineItem {LineItem}
   *        The current line item at play.
   *
   * @return {LineItem} The updated line item with the adjusted price.
   */
  private getAdjustedLineItemPrice(
    adjustedPrice: string | number,
    lineItem: LineItem
  ) {
    const customer = selectSaleCustomer(this.$ngRedux.getState())
    const taxRate = calculateTotalTaxRate(lineItem.taxInfo?.rates || [])
    const priceComponents = calculatePriceComponents(
      adjustedPrice,
      taxRate,
      this.registerManager.isTaxInclusive()
    )

    const adjustedLineItem: LineItem = {
      ...lineItem,
      // Since either the display price or discount price has been changed, set the `adjusted` to true
      adjusted: true,
      // Ensure currently referenced line item is updated with newly adjusted price.
      price: priceComponents.price,
      tax: priceComponents.tax,
    }

    // In training-mode will trigger the next step
    if (this.posStateManager.getTrainingModeEnabled()) {
      let priceAdjustedEvent = 'sale:line-item-price-adjusted:'

      if (isValidCustomer(customer)) {
        priceAdjustedEvent += 'has-customer'
      } else {
        priceAdjustedEvent += 'no-customer'
      }

      $rootScope.$emit(priceAdjustedEvent, adjustedLineItem)
    }

    return adjustedLineItem
  }

  /**
   * Save payment against the sale.
   *
   * @param paymentAmount {Number}
   *        The payment amount made.
   *
   * @param paymentType {PaymentType}
   *        The type of payment (Cash, Credit Card, etc).
   *
   * @param {Object} [transactionData]
   *        Additional data regarding the transaction. Currently used to add source and source id information
   *        relating to gift cards transactions.
   *
   * @param  paymentId {String}
   *         The id of the current payment.
   *
   * @return {Promise<Sale>} A promise resolved with the udpated sale, once the change for the sale has
   *                         been determined.
   */
  public pay(
    paymentAmount: string | number,
    paymentType: ApiRetailerPaymentType,
    transactionData?: ExternalPaymentData,
    paymentId?: string,
    tipAmount?: number | string,
    surchargeData?: SurchargeTenderProperties
  ) {
    const registerId = this.posStateManager.getRegisterId()!
    const registerSequenceId = this.registerManager.getActiveRegister()
      .register_open_sequence_id!

    // If the sale has any refundable surcharge, current payment is a refund, calculate the surcharge from the sale
    // When refunding money with any surcharge, the register prorates the refundable surcharge
    //    and sends surcharge inclusive payment amount
    // Therefore, the register should put the surcharge data to the payment when the payment is saved into the sale
    if (
      hasSurchargeRefundable(selectCurrentSale(this.$ngRedux.getState())) &&
      vn(paymentAmount).lt(0)
    ) {
      const sale = selectCurrentSale(this.$ngRedux.getState())
      const surchargeAmount = calculatePaymentSurchargeForRefund(
        paymentAmount,
        sale.finals.surchargeDisplayProperties.surcharge,
        sale.finals.inclusivePrice,
        getOutstandingBalance(sale),
        totalSurchargeFromPayments(sale.payments)
      )
      if (surchargeAmount) {
        surchargeData = {
          amount: surchargeAmount,
          rule: 'refund',
        }
      }
    }

    const payment = createPayment(
      paymentAmount,
      paymentType,
      registerId,
      registerSequenceId,
      transactionData,
      paymentId,
      tipAmount,
      surchargeData
    )
    this.$ngRedux.dispatch(addPayment(payment))

    // payment with tip need to sync with adjustments
    const tip = vn(tipAmount)
    if (tip.gt(0)) {
      this.$ngRedux.dispatch(addTip(tip.toNumber()))
    }

    if (surchargeData) {
      const surcharge = vn(surchargeData?.amount)
      if (surcharge.gt(0) && surchargeData.rule !== undefined) {
        this.$ngRedux.dispatch(
          addSurcharge({ ...surchargeData, amount: surcharge.toNumber() })
        )
      }
    }

    // Handle adding payAccountBalance payment
    if (isABPAddedButNotPaid(selectCurrentSale(this.$ngRedux.getState()))) {
      this.$ngRedux.dispatch(updatePayAccountBalanceWithPayment(payment))
    }

    /**
     * Adjust the customer's Account balance if the payment is for an On Account or Layby sale
     */
    if (isConfirmed({ status: selectSaleStatus(this.$ngRedux.getState()) })) {
      const sale = selectCurrentSale(this.$ngRedux.getState())
      // Account balance excludes any tips, so remove any tips from the payment amount
      const balanceToDeduct =
        sale.paidBalance && vn(sale.paidBalance).gt(0)
          ? sale.paidBalance
          : paymentAmount
      const adjustedPayment = subtract(vn(balanceToDeduct), vn(tipAmount))
      const account = add(sale.customerBalances!.account, adjustedPayment)
      this.$ngRedux.dispatch(updateCustomerBalances({ account }))
    }

    // We must update the sale finals before determining change for the sale. Otherwise the inclusivePrice could
    // still include the non cash fee (when dealing with cash discounting).
    this.priceManager.totalSale(
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )

    return this.determineChangeForSale(paymentType).then(() => {
      $log.debug(
        '[Sale Manager] pay',
        selectSalePayments(this.$ngRedux.getState())
      )
      return this.saveSale()
    })
  }

  /**
   * Responsible for determining the change amount and associated payment type for a sale. If cash management is not
   * enabled, use the payment type of the last given payment, otherwise use the payment type associated with cash
   * management.
   *
   * @param lastUsedPaymentType {paymentType}
   *        The type of payment (Cash, Credit Card, etc)
   *
   * @return {Promise} A promise resolved once the change for the sale has been determined.
   */
  public determineChangeForSale(lastUsedPaymentType: ApiRetailerPaymentType) {
    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())

    return new Promise<void>((resolve, reject) => {
      // If the sale has an account balance payment - add the amount to the inclusive price
      // of the sale to determine the inclusive price to calculate change for the sale.
      const inclusivePrice = sale.payAccountBalance?.amount
        ? VendNumber.add(
            sale.finals.inclusivePrice,
            sale.payAccountBalance.amount
          )
        : sale.finals.inclusivePrice
      let changeAmount = 0

      // We only calculate change if the sale is not a return / negative total
      if (vn(inclusivePrice).gte(0)) {
        const paymentsTotal = getPaymentsTotal(sale.payments)
        changeAmount = Math.min(
          0,
          VendNumber.subtract(inclusivePrice, paymentsTotal)
        )
      }

      if (vn(changeAmount).lt(0)) {
        const registerId = this.posStateManager.getRegisterId()!
        const registerSequenceId = this.registerManager.getActiveRegister()
          .register_open_sequence_id!
        let changePaymentTypeDeferred: IPromise<
          ApiRetailerPaymentType | undefined
        > = Promise.resolve(lastUsedPaymentType)

        if (this.cashMovementsManager.isCashManagementEnabled()) {
          const cashManagementPaymentTypeId = this.cashMovementsManager.getCashManagementPaymentTypeId()
          changePaymentTypeDeferred = paymentTypesStore.get(
            cashManagementPaymentTypeId!,
            true
          )
        }

        changePaymentTypeDeferred.then(changePaymentType => {
          const changePayment = createChangePayment({
            registerId,
            registerSequenceId,
            paymentType: changePaymentType!,
            amount: changeAmount,
          })

          this.$ngRedux.dispatch(setChange(changePayment))
          resolve()
        })
      } else {
        this.$ngRedux.dispatch(setChange(null))
        resolve()
      }
    })
  }

  /**
   * Void the current sale.
   *
   * @return {Promise} A promise resolved once the sale has been voided and a new sale created
   */
  public voidSale(): Promise<void | Sale> {
    $log.debug(
      '[Sale Manager] voiding sale',
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )
    return this.transitionSaleToStatus(SaleStatus.VOIDED).then(() =>
      $q.when(this.finaliseSale())
    )
  }

  /**
   * Make a layby sale.
   *
   * @return {Promise} A promise resolved once the sale status has been set to layby
   */
  public laybySale() {
    $log.debug(
      '[Sale Manager] layby sale',
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )
    return this.transitionSaleToStatus(SaleStatus.LAYBY_SALE)
  }

  /**
   * Make an on account sale.
   *
   * @return {Promise} A promise resolved once the sale status has been set to on account
   */
  public onAccountSale() {
    $log.debug(
      '[Sale Manager] on account sale',
      selectCurrentSaleAsSale(this.$ngRedux.getState())
    )
    return this.transitionSaleToStatus(SaleStatus.ACCOUNT_SALE)
  }

  /**
   * Park the current sale.
   *
   * @return {Promise} A promise resolved once the sale has been parked and a new sale created
   */
  public async parkSale(): Promise<void> {
    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    $log.debug('[Sale Manager] parking sale', sale)
    try {
      await this.transitionSaleToStatus(SaleStatus.PARKED)
      await this.finaliseSale()

      // TODO: Remove and replace with react toast
      wrToastSuccess('park.sale.success')
    } catch (err) {
      if (typeof err === 'string' && err === PARK_SALE_FAILED_ERROR_MSG) {
        trackError(`[Sale Manager] ${err}`, sale)
      } else {
        trackError('[Sale Manager] error happened when parking sale', err)
      }
      return Promise.resolve()
    }
  }

  /**
   * Quote the current sale.
   *
   * @return {Promise} A promise resolved once the sale has been placed in a 'quote' status
   */
  public quoteSale() {
    return $q.when(this.transitionSaleToStatus(SaleStatus.QUOTE))
  }

  /**
   * Mark the current sale as unfulfilled - with pickup.
   *
   * @return {Promise} A promise resolved once the sale has been placed in a 'unfulfilled pickup' status
   */
  public unfulfilledPickUp() {
    return this.transitionSaleToStatus(SaleStatus.AWAITING_PICKUP)
  }

  /**
   * Mark the current sale as unfulfilled - with delivery.
   *
   * @return {Promise} A promise resolved once the sale has been placed in a 'unfulfilled dispatch' status
   */
  public unfulfilledDelivery() {
    return this.transitionSaleToStatus(SaleStatus.AWAITING_DISPATCH)
  }

  /**
   * Close the current sale.
   *
   * @return {Promise} A promise resolved once the sale has been closed and a new sale created
   */
  public closeSale() {
    const saleStatus = selectSaleStatus(this.$ngRedux.getState<RootState>())

    let closedSaleStatus = SaleStatus.CLOSED

    // Transition to a different closed status for layby or onaccount sales
    if (saleStatus === SaleStatus.LAYBY_SALE) {
      closedSaleStatus = SaleStatus.LAYBY_SALE_CLOSED
    } else if (saleStatus === SaleStatus.ACCOUNT_SALE) {
      closedSaleStatus = SaleStatus.ACCOUNT_SALE_CLOSED
    } else if (
      saleStatus === SaleStatus.AWAITING_PICKUP ||
      saleStatus === SaleStatus.AWAITING_DISPATCH ||
      saleStatus === SaleStatus.SERVICE
    ) {
      closedSaleStatus = saleStatus
    }

    this.$ngRedux.dispatch(saleClosed())

    const activeRegister = this.registerManager.getActiveRegister()
    const seqIds = getFromStorage<Record<string, string | null>>(
      'VEND_REGISTER_OPEN_SEQUENCE'
    )
    if (seqIds) {
      const persistedOpenSeqId = seqIds[activeRegister.id]
      const currentOpenSeqId = activeRegister.register_open_sequence_id

      if (
        typeof persistedOpenSeqId !== 'undefined' &&
        persistedOpenSeqId !== currentOpenSeqId
      ) {
        // Submit UBE
        this.vdTracking.trackUserEvent({
          eventName: 'Register open sequence ID mismatch',
          metadata: {
            persistedOpenSequenceID: persistedOpenSeqId,
            currentOpenSequenceID: currentOpenSeqId,
          },
        })
      }
    }

    return this.transitionSaleToStatus(closedSaleStatus)
  }

  /**
   * If taxGroupId not provided, remove all tax rates from a sale
   * If taxGroupId provided, remove all tax rates from line items belongs to that tax (or taxgroup)
   * @param {string} taxGroupId - the id of the taxGroup (or tax) to remove its rates
   * @return {Promise} A promise resolved once the sale has been saved
   */
  public removeAllTax(taxGroupId?: string): Promise<void | Sale> {
    const { getState, dispatch } = this.$ngRedux
    const lineItems = selectLineItems(getState())

    const filteredLineItems = taxGroupId
      ? lineItems.filter(lineItem => lineItem.taxInfo?.id === taxGroupId)
      : lineItems

    // Submit UBE
    const saleFinals = selectSaleFinals(getState())
    const totalNumberOfTaxRates = Object.keys(saleFinals.taxes).length

    const taxRateIdsToBeRemoved = filteredLineItems.reduce(
      (rateIds: Set<string>, lineItem: LineItem) => {
        lineItem.taxInfo?.rates.forEach(rate => rateIds.add(rate.id))
        return rateIds
      },
      new Set<string>()
    )
    this.trackTaxRateRemoval(
      Array.from(taxRateIdsToBeRemoved),
      totalNumberOfTaxRates,
      taxGroupId
    )

    // Get the noTaxGroup, apply it to all filtered lineItems, then save the sale
    return this.taxManager
      .getNoTaxGroup()
      .then(noTaxGroup => {
        const updatedLineItems = filteredLineItems.map((lineItem: LineItem) => {
          return {
            ...lineItem,
            taxInfo: noTaxGroup,
            adjusted: true,
            customerTaxApplied: false,
          }
        })

        dispatch(updateLineItems(updatedLineItems))
      })
      .then(() => this.queueSaleCalculation())
  }

  /**
   * Remove a tax rate from a sale.
   * @param taxRate {VendTax}
   * @param {string} taxGroupId - the id of the taxGroup (or tax) to remove the rate
   * @return {Promise} A promise resolved once the sale has been saved
   */
  public removeTax(taxRate: { id: string }, taxGroupId?: string) {
    const { getState } = this.$ngRedux
    // Submit UBE
    const saleFinals = selectSaleFinals(getState())
    const totalNumberOfTaxRates = Object.keys(saleFinals.taxes).length
    this.trackTaxRateRemoval([taxRate.id], totalNumberOfTaxRates, taxGroupId)

    // Wait for all line items to have the tax removed
    const lineItems = selectLineItems(getState())
    const filteredLineItems = taxGroupId
      ? lineItems.filter(lineItem => lineItem.taxInfo?.id === taxGroupId)
      : lineItems

    return Promise.all(
      filteredLineItems.map(lineItem => {
        return this.removeTaxFromLineItem(taxRate, lineItem)
      })
    )
      .then(() => {
        // Then save the sale, we don't need to check that any of the tax rates
        // have actually been removed, since this should only be called if
        // the tax rate exists at least once.
        return this.queueSaleCalculation()
      })
      .catch(error => {
        trackError('[SaleManager] Error removing tax', { data: { error } })
      })
  }

  /**
   * Generate a UBE with details of tax rate removal.
   *
   * @param taxRatesIdsToBeRemoved
   * @param totalNumberOfTaxRatesPriorToRemoval
   * @param {string} taxGroupId - id of the tax group (or tax) containing the rates to be removed
   */
  private trackTaxRateRemoval(
    taxRatesIdsToBeRemoved: string[],
    totalNumberOfTaxRatesPriorToRemoval: number,
    taxGroupId?: string
  ) {
    this.vdTracking.trackUserEvent({
      eventName: 'transaction.sale.remove.taxrate',
      metadata: {
        taxRatesIdsToBeRemoved,
        totalNumberOfTaxRatesPriorToRemoval,
        taxId: taxGroupId,
      },
    })
  }

  /**
   * Remove a tax rate from a lineItem.
   *
   * @param taxRate {VendTax}
   * @param lineItem
   *
   * @return {Promise} A promise resolved once the tax is removed
   */
  private removeTaxFromLineItem(
    taxRate: {
      id: string
    },
    lineItem: LineItem
  ): Promise<void> {
    const rates = lineItem.taxInfo!.rates

    return new Promise((resolve, reject) => {
      // Tax Groups should have at least one rate
      if (rates.length <= 0) {
        return reject(new Error('Tax group had no rates'))
      }

      // For three or more rates, reject if the rate is present
      if (rates.length >= 3) {
        if (find(rates, { id: taxRate.id })) {
          return reject(
            new Error(
              'Cannot remove a single tax from a tax group with three or more rates'
            )
          )
        }
        return resolve()
      }

      // If we have one rate, and it's the rate to remove,
      // replace it with the "no tax" tax group
      if (rates.length === 1) {
        if (lineItem.taxInfo!.rates[0].id === taxRate.id) {
          return this.taxManager
            .getNoTaxGroup()
            .then(noTaxGroup => {
              this.$ngRedux.dispatch(
                updateLineItemTax({
                  lineItemLocalID: lineItem.localId,
                  adjusted: true,
                  customerTaxApplied: false,
                  taxInfo: noTaxGroup,
                })
              )
            })
            .then(resolve)
        }
        return resolve()
      }

      // replacementRateId will store the new tax rate id for the sale
      // item, if the tax to be removed is found.
      let replacementRateId: string

      if (rates[0].id === taxRate.id) {
        // If the first rate is the one to be removed, replacement id is
        // the second rate.
        replacementRateId = rates[1].id
      } else if (rates[1].id === taxRate.id) {
        // If the second rate is the rate to be removed, replacement id is the
        // first rate.
        replacementRateId = rates[0].id
      } else {
        // This group didn't contain the rate.
        return resolve()
      }

      // Replace the taxInfo with the new rate
      return this.taxesStore
        .getTaxByRateId(replacementRateId)
        .then(
          rate => {
            this.$ngRedux.dispatch(
              updateLineItemTax({
                lineItemLocalID: lineItem.localId,
                adjusted: true,
                taxInfo: rate,
                customerTaxApplied: false,
              })
            )
          },
          error => {
            trackError(
              '[SaleManager] Error fetching tax rate after removing tax from line item',
              {
                data: {
                  error,
                  lineItem,
                  replacementRateId,
                },
              }
            )
          }
        )
        .then(resolve)
    })
  }

  /**
   * Set the tax on a lineItem.
   *
   * @param lineItem {LineItem}
   * @param taxId {string}
   *
   * @return {Promise<LineItem>} A promise resolved once the tax is updated
   */
  public async setTaxOnLineItem(lineItem: LineItem, taxId: string) {
    if (lineItem.taxInfo?.id === taxId) {
      return lineItem
    }

    const tax = await this.taxesStore.get(taxId)
    if (!tax) {
      throw Error(`Tax with id ${taxId} was not found`)
    }

    const lineItemWithTax = {
      ...lineItem,
      taxInfo: tax,
    }
    this.$ngRedux.dispatch(updateLineItem(lineItemWithTax))
    await this.queueSaleCalculation()

    return lineItemWithTax
  }

  /**
   * Transition the current sale to the given state (CLOSED, VOIDED, PARKED, etc). This involves setting
   * things like the user ID, sale date, register ID etc.
   *
   * @param  status {String}
   *         The status (CLOSED, VOIDED, PARKED, etc) to transition the sale to
   *
   * @return {Promise} A promise resolved once the sale has been transitioned
   */
  private transitionSaleToStatus(status: SaleStatus): Promise<Sale> {
    // TODO(refactor): This function is doing way too much
    let sale: Readonly<ReduxCurrentSale> = this.$ngRedux.getState<RootState>()
      .currentSale
    const activeRegisterId = this.registerManager.getActiveRegister().id
    const isReturnFromDifferentRegister =
      isReturn(sale) && sale.registerId !== activeRegisterId

    // For training mode
    $rootScope.$emit('sale:transitioning-to-status', {
      sale,
      status,
    })

    // If sale was previously parked or is about to be parked, we clear the user, register & sale date so that
    // these properties can be regenerated prior to posting to the server
    if (
      sale.status === SaleStatus.PARKED ||
      status === SaleStatus.PARKED ||
      status === SaleStatus.QUOTE
    ) {
      this.$ngRedux.dispatch(parkedOrQuoteTransitioned())
    }

    this.$ngRedux.dispatch(updateSaleStatus(status))
    sale = this.$ngRedux.getState<RootState>().currentSale

    // Set the local sale as ready to be completed when we transition the status to be either a layby or
    // onaccount sale. Set the salesItems and lineItems as confirmed
    if (
      sale.status === SaleStatus.LAYBY_SALE ||
      sale.status === SaleStatus.ACCOUNT_SALE
    ) {
      const lineItems = selectLineItems(this.$ngRedux.getState())
      lineItems.forEach(item => {
        this.$ngRedux.dispatch(
          updateLineItem({
            ...item,
            confirmed: true,
          })
        )
      })

      this.$ngRedux.dispatch(saleReadyToBeCompleted())

      if (sale.customerBalances) {
        /**
         * If the sale is a new On Account or Layby sale that has affected the customer's Account
         * Balance, we need to manually calculate the correct value because it has not been
         * updated yet
         */

        const prevOnAccountBalance = sale.customerBalances.account

        const newOnAccountBalance = getAdjustedAccountBalance(sale)

        const balanceSpend = prevOnAccountBalance - newOnAccountBalance

        const newRemainingOnAccountLimit = VendNumber.add(
          sale.customer?.on_account_limit ?? 0,
          newOnAccountBalance
        )

        // UBE - Tracking when merchant overspends on account limit
        if (newRemainingOnAccountLimit < 0) {
          const oldLimitRemaining =
            sale.customerBalances.availableOnAccountLimitBalance
          const overSpend =
            oldLimitRemaining <= 0
              ? balanceSpend
              : balanceSpend - oldLimitRemaining

          this.vdTracking.trackUserEvent({
            eventName: 'On Account Limit Overspend',
            metadata: {
              overspend: overSpend,
              newTotalLimitRemaining: newRemainingOnAccountLimit,
            },
          })
        }

        this.$ngRedux.dispatch(
          updateCustomerBalances({
            account: newOnAccountBalance,
            availableOnAccountLimitBalance: newRemainingOnAccountLimit,
          })
        )
      }
    } else if (sale.payAccountBalance?.amount && sale.customerBalances) {
      this.$ngRedux.dispatch(accountBalancePaymentAdded())
    }

    this.$ngRedux.dispatch(displayStatusChanged())

    if (!sale.saleDate) {
      this.$ngRedux.dispatch(dateChanged())
    }

    // @note receipt number is assigned to sale at end of sale intentionally. This is to avoid incorrect receipt
    // number generation on parked sales etc. A sale should only be assigned one once it's had payment against it.
    // Also reset the receipt number details if the return originated from the another register (in that instance the
    // receipt number would have been an increment from the original register)
    if (!sale.receiptNumberDetails || isReturnFromDifferentRegister) {
      if (status !== SaleStatus.QUOTE) {
        // If the sale is a quote, we don't want to generate and increment the receipt number
        this.$ngRedux.dispatch(
          receiptNumberDetailsChanged(
            this.registerManager.getNextReceiptNumberDetailsAndIncrement()
          )
        )
      }
    }

    if (!sale.registerId) {
      this.$ngRedux.dispatch(
        registerChanged(this.posStateManager.getRegisterId())
      )
    }

    if (!sale.user) {
      // getLoggedInUser returns a VdUserModel which is non serialisable,
      // so we need to convert it to the local sale User type
      const saleUser = apiUserToSaleUser(this.userManager.getLoggedInUser())
      this.$ngRedux.dispatch(userChanged(saleUser))
    }

    return this.saveSale()
  }

  public newSale() {
    const sale = selectCurrentSale(this.$ngRedux.getState())

    if (hasLineItems(sale) || isValidCustomer(sale.customer) || sale.note) {
      this.completeSaleTracking()
    }

    $rootScope.$emit('sale:new')
    this.emit('EventEmitter:sale:new')

    this._setActiveSale()
    this.saleInventoryData.reset()

    this.posStateManager.setActiveSaleLocalId(null)
    this.hotkeysService.startCountingHotkeys()
  }

  /**
   * Finalise all sale tracking data before a new sale is created.
   */
  public completeSaleTracking() {
    const {
      returnFor,
      serviceFields,
      customFields,
      status,
    } = selectCurrentSaleAsSale(this.$ngRedux.getState())
    const metadata: {
      isReturn: boolean
      shortcuts: number
      status?: string
      hasServiceOrder?: boolean
    } = {
      isReturn: Boolean(returnFor && returnFor.length > 0),
      shortcuts: this.hotkeysService.stopCountingHotkeys(),
      status,
    }
    if (hasServiceOrder(serviceFields, customFields)) {
      metadata.hasServiceOrder = true
    }
    this.saleTracker.completeTracking(metadata)
  }

  /**
   * Discards the current sale and then creates a new, blank sale.
   */
  public clearSale() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState<RootState>())
    return new Promise<void>((resolve, reject) => {
      if (currentSale && currentSale.localId) {
        this.salesStore
          .removeSale(currentSale.localId)
          .then(() => {
            $log.debug('[Sale Manager] clear sale', currentSale)
            return this.newSale()
          })
          .then(() => {
            this.$ngRedux.dispatch(resetSaleCustomerContactInfoReviewStatus())
          })
          .then(resolve, reject)
      } else {
        resolve() // There is no sale so just resolve
      }
    })
  }

  /**
   * Discards the current sale and emits the training sale complete event (which then restarts the training tour).
   *
   * @return {Promise} A promise resolved once the sale has been removed
   */
  public clearTrainingModeSale() {
    return this.clearSale().then(() => {
      $rootScope.$emit('training-sale:complete')
    })
  }

  /**
   * Either save the sale and post it to the server.
   *
   * @return {Promise} A promise resolved once the sale has been saved and a
   * new sale created
   */
  public finaliseSale(): Promise<void> {
    const localStorageCloseSaleSetting = getFromStorage<{
      closeAfterComplete?: boolean
    }>(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE)
    const sale: Sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    this.closeAfterComplete = localStorageCloseSaleSetting
      ? Boolean(localStorageCloseSaleSetting.closeAfterComplete)
      : false
    if (!sale.status) {
      trackError(
        '[Sale Manager] No sale status when attempting to finalise sale',
        { data: { sale_id: sale.id || sale.localId } }
      )
      return Promise.resolve()
    }
    // Potentially track other properties for training mode sales
    if (this.posStateManager.getTrainingModeEnabled()) {
      this.vdTracking.trackUserEvent({
        eventName: 'Training Sale',
        metadata: {
          Status: sale.status,
        },
      })

      if (this.posStateManager.getTrainingModeOnboardingState()) {
        this.vdTracking.trackUserEvent({
          eventName: 'Retailer On-Boarding Tour',
          metadata: {
            step: 'Sale Completed',
          },
        })
      }

      return this.clearTrainingModeSale()
    }

    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_COMPLETE,
      data: {
        sale: this.saleTransformer.toClientApi(sale, sale.lineItems),
      },
    })

    this.$ngRedux.dispatch(finaliseSaleSyncStatus())

    return this._saveSale()
      .then(() => {
        if (this.closeAfterComplete) {
          return this.saleSyncManager
            .postSale(sale.localId)
            .then(() => this.productsStore.removeDeletedProducts())
            .then(() => $window.close())
            .catch(() =>
              $log.debug(
                '[Sale Manager] Sale posting failed, did not auto close the tab'
              )
            )
            .finally(() =>
              removeFromStorage(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE)
            )
        } else {
          this.saleSyncManager
            .postSale(sale.localId)
            .then(() => this.productsStore.removeDeletedProducts())
        }
      })
      .finally(() => this.newSale())
  }

  /**
   * Determine if we should send the sale to the API earlier.
   *
   * @method shouldSubmitSaleEarly
   *
   * @return {Boolean} True if the sale should be sent to the API earlier, false otherwise
   */
  public shouldSubmitSaleEarly() {
    const trainingModeEnabled = this.posStateManager.getTrainingModeEnabled()

    // We are going to submit the sale early if the sale is not a quote, sale is not training mode,
    // and the feature flag is enabled.
    return (
      !trainingModeEnabled &&
      !isQuote(selectSaleStatus(this.$ngRedux.getState()))
    )
  }

  /**
   * Save the sale and post it to the server. This is used instead of finaliseSale
   * when we separate saving the sale and completing it into two different steps.
   *
   * @return {Promise} A promise resolved once the sale has been saved
   */
  public submitSale(): IPromise<void | Sale> {
    const sale = selectCurrentSale(this.$ngRedux.getState())
    if (!sale.status) {
      trackError(
        '[Sale Manager] No sale status when attempting to finalise sale',
        { data: { sale_id: sale.id || sale.localId } }
      )
      return $q.resolve()
    }

    // Clear the local readyToComplete property for layby and on account sales
    if (isConfirmed(sale)) {
      this.$ngRedux.dispatch(setReadyToComplete(false))
    }

    const lineItems = selectLineItems(this.$ngRedux.getState())
    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_COMPLETE,
      data: {
        sale: this.saleTransformer.toClientApi(sale, lineItems),
      },
    })
    this.$ngRedux.dispatch(setSyncStatus(SaleSyncStatus.UPLOAD_FIRST_SUBMIT))
    if (
      isMarkedAsUnfulfilled(sale.status) ||
      hasLineItemFulfilment(lineItems)
    ) {
      return $q.resolve(this.setPickableState()).then(() => {
        this.saveSaleEarly(sale.localId)
      })
    } else {
      return this.saveSaleEarly(sale.localId)
    }
  }

  /**
   * Update sale fields when submit sale early failed due to some reason with localId regenerated
   * @param {Sale} postSaleResult - the sale model returned in the callback
   */
  public updateSaleWhenLocalIdRegenerated(postSaleResult: Sale): void {
    if (!postSaleResult || !(postSaleResult instanceof Sale)) {
      return
    }
    const {
      localId,
      syncAttempts,
      syncStatus,
      receiptNumberDetails,
    } = postSaleResult
    this.$ngRedux.dispatch(
      localIdGenerated({
        localId,
        syncAttempts,
        syncStatus,
        receiptNumberDetails,
      })
    )
  }

  private saveSaleEarly(localId: string) {
    return this._saveSale().then(() => {
      return this.saleSyncManager
        .postSale(localId)
        .then(result => {
          if (result && result instanceof Sale) {
            // The whole sale model will be returned when it's with regenerated localId
            if (localId !== result.localId) {
              this.posStateManager.setActiveSaleLocalId(result.localId)
            }
            this.updateSaleWhenLocalIdRegenerated(result)
            localId = result.localId
          }
          // the sale sync status will be uploaded in iDB after a successfull submission of the sale
          // below block will keep the same value in indexedDB and salemanager.sale
          return this.salesStore.loadSale(localId).then(loadedSale => {
            const payload: {
              syncStatus?: SaleSyncStatus
              receiptNumberDetails?: string | ReceiptNumberDetails
            } = {}
            if (loadedSale?.syncStatus) {
              payload.syncStatus = loadedSale.syncStatus
            }
            if (
              loadedSale &&
              !isSameReceiptDetails(
                selectCurrentSale(this.$ngRedux.getState<RootState>())
                  .receiptNumberDetails,
                loadedSale.receiptNumberDetails
              )
            ) {
              const previousReceiptNumberDetails = loadedSale.getFormattedReceiptNumberDetails()
              payload.receiptNumberDetails = loadedSale.receiptNumberDetails
              this.vdTracking.trackUserEvent({
                eventName: 'Resolved duplicate invoice number',
                metadata: {
                  saleId: localId,
                  previousReceiptNumberDetails,
                  loadedReceiptNumberDetails: loadedSale.getFormattedReceiptNumberDetails(),
                },
              })
            }
            this.$ngRedux.dispatch(saveComplete(payload))
          })
        })
        .then(() => this.productsStore.removeDeletedProducts())
        .catch(() =>
          $log.debug(
            '[Sale Manager] Sale posting failed, during submit sale step'
          )
        )
        .finally(() => {
          this.emit('earlySubmitSaleComplete')
        })
    })
  }

  /**
   * On confirmation screen, update the UPLOADED_TO_COMPLETE sale if there is customer or note change,
   * then send email receipt and complete sale process.
   *
   * @return {Promise} A promise resolved once the sale has been updated and a new sale created
   */
  public completeSale(): IPromise<void | Sale> {
    const sale: ReduxCurrentSale = selectCurrentSale(this.$ngRedux.getState())

    const hasAddedCustomer = Boolean(sale.hasAddedCustomerOnConfirmation)
    const hasUpdatedSaleNote = Boolean(sale.hasUpdatedNoteOnConfirmation)
    const hasUpdatedCustomField = Boolean(
      sale.hasUpdatedCustomFieldsOnConfirmation
    )

    const localStorageCloseSaleSetting = getFromStorage<{
      closeAfterComplete?: boolean
    }>(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE)
    this.closeAfterComplete = localStorageCloseSaleSetting
      ? Boolean(localStorageCloseSaleSetting.closeAfterComplete)
      : false

    if (this.closeAfterComplete) {
      $window.close()
      removeFromStorage(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE)
    }

    const completeSalePromises: Array<
      Promise<void | Response | FetchResult>
    > = [postEmailReceipt(sale)]

    if (hasAddedCustomer || hasUpdatedSaleNote) {
      const saleUpdateInput = this.saleTransformer.formatUpdateSaleInput(
        sale,
        hasAddedCustomer,
        hasUpdatedSaleNote
      )
      // We only need to update the sale which has updated customer or note on Complete Screen
      completeSalePromises.push(doUpdateSale(saleUpdateInput))
    }

    if (hasUpdatedCustomField) {
      const lineItems = selectLineItems(this.$ngRedux.getState())
      const customFieldsUpdateInput = entityCustomFieldsToMap(sale, lineItems)
      completeSalePromises.push(...updateCustomFields(customFieldsUpdateInput))
    }

    this.$ngRedux.dispatch(setSyncStatus(SaleSyncStatus.UPLOADED))
    $q.all(completeSalePromises)
    return this.saveSale().then(() => this.newSale())
  }

  /**
   * Helper to get all the required data for printing receipts.
   * Retrieves base receipt data and gift card payments summary.
   * Retrieves retailers gift card settings to calculate expiry date of purchased gift cards in sell screen
   *
   * @return {Object} Data required for the receipt
   */
  public getReceiptData(): IPromise<ReceiptData> {
    const sale = selectCurrentSale(this.$ngRedux.getState())

    if (
      !sale.id && // It's a new sale
      sale.isSubmittedSaleEarly && // It's been submitted early
      !isUploadedToBeCompleted(sale) && // 1st submission failed
      !!sale.receiptNumberDetails // Only clear receiptNumberDetails if it is not empty
    ) {
      this.$ngRedux.dispatch(receiptNumberDetailsChanged(undefined))
      $q.when(this.saveSale())
    }
    const receiptData = this.getBaseReceiptData()
    return $q
      .all([
        this.paymentManager.getBalancesGiftCardPaymentsSummary(sale.payments),
        this.paymentManager.getBigCommerceGiftCertPaymentsSummary(
          sale.payments
        ),
      ])
      .then((giftCardPaymentsSummaries: any[]) => {
        // flatten the summaries from an array of arries into a single array.
        const giftCardPaymentsSummary = giftCardPaymentsSummaries.reduce(
          (arr, accum) => accum.concat(arr),
          []
        )
        const res = {
          ...receiptData,
          giftCardsInfo: { giftCardSettings: {}, giftCardPaymentsSummary },
        }
        // If we received some balances gift card summaries, get gift card settings.
        if (
          giftCardPaymentsSummaries.length > 0 &&
          giftCardPaymentsSummaries[0].length > 0
        ) {
          return this.BalancesResource.getRetailerGiftCardSettings().then(
            (giftCardSettings: any) => {
              res.giftCardsInfo.giftCardSettings = giftCardSettings
              return res
            }
          )
        }
        return res
      })
      .catch((error: any) => {
        $log.warn(
          '[Sale Manager] Failed to retrieve retailer gift card settings',
          error
        )
        return receiptData
      })
  }

  /**
   * Helper to get all the required data for printing gift receipts.
   *
   * @return {Object} Data required for the receipt
   */
  public getBaseReceiptData(): ReceiptData {
    const currentSale = selectCurrentSaleAsSale(this.$ngRedux.getState())

    return {
      trainingModeEnabled: this.posStateManager.getTrainingModeEnabled(),
      sale: currentSale,
      register: this.registerManager.getActiveRegister(),
      outlet: this.registerManager.getActiveOutlet(),
      receiptHtmlExtra: this.paymentManager.paymentIntegrationReceiptMessage,
    }
  }

  public getSurchargeLabel(): string {
    const sale = selectCurrentSale(this.$ngRedux.getState<RootState>())
    return getSurchargeLabel(sale)
  }

  // ============================== Private API ============================== //

  /**
   * Perform initialisation of the sale manager, loading any saved sale from the database.
   *
   * @return {Promise<Sale>} A promise resolved with the active Sale once the sale manager is ready
   */
  private _init() {
    this.isInitialised = true
    this.syncManager.on('syncComplete', () => this.queueSaleCalculation())
    this.productsStore.on('sync', this.onProductsStoreSync, this)
    this.customerTaxesStore.on('sync', this.onCustomerTaxesSync, this)

    return this.loadOrCreateSale()
  }

  /**
   * Set the given sale (or a newly created one) as active. Updates the currentSale state in Redux accordingly.
   *
   * @param sale The Sale to set as active. If no sale is provided a new empty Sale will be created.
   */
  private _setActiveSale(sale?: Sale) {
    if (sale) {
      this.$ngRedux.dispatch(setCurrentSale(sale))
    } else {
      const localId = uuid()
      this.$ngRedux.dispatch(newCurrentSale(localId))
    }
  }

  /**
   * Responds to sync events that occur on the Products store.
   *
   * @param {Object} data
   *
   *        @param {Array} data.removed
   *               Collection of products removed from the products store
   */
  private onProductsStoreSync(
    data: {
      removed?: ApiProduct[]
    } = {}
  ) {
    if (!data.removed || !data.removed.length) {
      return
    }

    const lineItems = selectLineItems(this.$ngRedux.getState())
    // Set the deleted_at property for products that have been deleted but which are in the active sale. Setting this
    // will allow the line item to be highlighted as being deleted
    lineItems.forEach(lineItem => {
      const lineItemProduct = lineItem.product
      const match = find(data.removed, { id: lineItemProduct.id })

      if (match) {
        this.$ngRedux.dispatch(
          updateLineItem({
            ...lineItem,
            ...{
              product: { ...lineItemProduct, deleted_at: match.deleted_at },
            },
          })
        )
      }
    })
  }

  /**
   * Responds to sync events that occur on the CustomerTaxes store.
   *
   * @param {Object} syncedData
   *
   *        @param {Array} data.updated
   *               Collection of customer taxes updated from the CustomerTaxes store
   *        @param {Array} data.removed
   *               Collection of customer taxes removed from the CustomerTaxes store
   */
  private onCustomerTaxesSync(
    syncedData: {
      updated?: ApiCustomerTax[]
      removed?: ApiCustomerTax[]
    } = {}
  ) {
    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    if (
      !sale.customer ||
      isClosed(sale.status) || // to prevent sale recalculation on confirmation screen
      (!syncedData?.updated?.length && !syncedData?.removed?.length)
    ) {
      return
    }

    const isCustomerTaxUpdated = syncedData.updated?.some(
      (customerTax: ApiCustomerTax) =>
        customerTax.customer_id === sale.customer!.id
    )
    const isCustomerTaxRemoved = syncedData.removed?.some(
      (customerTax: ApiCustomerTax) =>
        customerTax.customer_id === sale.customer!.id
    )
    // Only recalculate the sale when the tax of current attached customer is changed
    if (isCustomerTaxUpdated || isCustomerTaxRemoved) {
      this.priceManager.recalculateSale(sale)
    }
  }

  /**
   * Check if there is a sale saved locally and load it from IndexedDB if there is, otherwise create a new
   * blank sale.
   *
   * @return {Promise<Sale>|Sale} Either a new Sale or a promise resolved with the loaded Sale once loaded
   */
  private loadOrCreateSale() {
    const saleLocalId = this.posStateManager.getActiveSaleLocalId()

    if (saleLocalId) {
      return this.saleLoader
        .loadSale(saleLocalId)
        .then((sale: Sale) => {
          if (sale) {
            // Ensure that we don't change the sync status of on an account or layby sale from "unmodified" to
            // "modified" when setting the active sale.
            return this.setActiveSale(sale, {
              unconfirmedSalesOnly: isConfirmedNotReadyToComplete(sale),
            })
          } else {
            this.newSale()
            return
          }
        })
        .then(() => this.queueSaleCalculation())
        .catch(() => this.newSale())
    } else {
      return this.newSale()
    }
  }

  /**
   * Mark the current sale as modified unless it is already completed (or it is confirmed and
   * `unconfirmedSalesOnly` is set).
   *
   * @param  [unconfirmedSalesOnly=false] {Boolean}
   *         Only set unconfirmed (i.e. not Layby/On Account/Unfulfilled) sales as modified prior to saving
   */
  private setSaleModified(options?: { unconfirmedSalesOnly: boolean }) {
    const currentSale = selectCurrentSale(this.$ngRedux.getState<RootState>())
    const unconfirmedSalesOnly = options && options.unconfirmedSalesOnly

    if (
      !isCompleted(currentSale.status) &&
      (!unconfirmedSalesOnly ||
        (!isConfirmed(currentSale) && !currentSale.isSavedUnfulfilled))
    ) {
      // We don't want to update the sync status of a sale that is already submitted to the server.
      // This can happen on the confirmation screen where we try to submit the sale when the screen loads.
      // (This behaviour is behind the `send_sale_to_api_earlier` FF)
      // The sync status of such a sale will be UPLOADED/UPLOAD, we don't want to change it to MODIFIED
      if (!currentSale.isSubmittedSaleEarly) {
        this.$ngRedux.dispatch(setSyncStatus(SaleSyncStatus.MODIFIED))
      }
    }
  }

  /**
   * Save the managed sale to IndexedDB and register it as the active sale in the POS state manager.
   *
   * @return {Promise<Sale>} A promise resolved with the sale once it has been saved to IndexedDB
   */
  private _saveSale(): Promise<Sale> {
    if (!this.saleTracker.isTrackingInitialised()) {
      this.saleTracker.initialiseTracking()
    }
    const state = this.$ngRedux.getState<RootState>()
    const currentSale = selectCurrentSale(state)
    const lineItems = selectLineItems(state)

    const saleStorageAdapter = new ReduxSaleStorageAdapter(
      currentSale,
      lineItems
    )
    return this.salesStore.saveSale(saleStorageAdapter).then(() => {
      this.posStateManager.setActiveSaleLocalId(currentSale.localId)
      const state = this.$ngRedux.getState<RootState>()

      return selectCurrentSaleAsSale(state)
    })
  }

  /**
   * Check whether the sale manager has an active sale.
   *
   * @return {Boolean} True if there is an active sale
   */
  public hasActiveSale() {
    return Boolean(
      this.posStateManager.getActiveSaleLocalId() &&
        selectCurrentSale(this.$ngRedux.getState())
    )
  }

  /**
   * Save the existing sale appropriately and remove it as the current active sale. Park the sale if it's status is
   * unconfirmed; place it back on layby or on account otherwise.
   *
   * @return {Promise} Resolved once the existing sale has been saved.
   */
  private saveAndFinaliseCurrentSale() {
    const sale = selectCurrentSale(this.$ngRedux.getState())
    if (!isActiveSale(sale)) {
      return $q.resolve()
    }

    // Saved not fully paid unfulfilled sale and  not fully paid service
    if (sale.isSavedUnfulfilled || sale.isSavedService) {
      return $q.when(this.finaliseSale()).then(() =>
        this.vdToastNotificationService.success({
          message: 'Your sale was successfully saved.',
        })
      )
    }

    const isConfirmed = isConfirmedSale(sale)

    if (!isConfirmed) {
      // Non "Confirmed Sales" (Layby or On Account) can be parked
      // Users have the option to retrieve sales history even if the current sale might have an active transaction after closing the tab or if the browser crashes.
      // We'll monitor the behavior of choosing "Save and Continue" option to gain insights and effectively handle such scenarios
      const {
        intentId,
        registerSalePaymentId,
      } = this.paymentManager.checkAndRetriveActivePayment(sale.localId)
      if (registerSalePaymentId) {
        // Submit UBE
        this.vdTracking.trackUserEvent({
          eventName:
            'Skip LSPAY Transaction recovery: Opting to proceed with alternative sales',
          metadata: {
            paymentIntentId: intentId,
            registerSalePaymentId,
            saleId: sale.localId,
          },
        })
      }
      return $q.when(this.parkSale())
    }

    // "Confirmed Sales" should be saved appropriately
    const actionConfirmedSaleResolver = $q.when(
      sale.status === SaleStatus.LAYBY_SALE
        ? this.laybySale()
        : this.onAccountSale()
    )

    return actionConfirmedSaleResolver
      .then(() => $q.when(this.finaliseSale()))
      .then(() =>
        this.vdToastNotificationService.success({
          message: 'Your sale was successfully saved.',
        })
      )
  }

  /**
   * Dynamically sets the debounce delay for the sale calculation queue; lowering the delay if there are no
   * active promotions or within a test env.
   *
   * @return {Number} The debounce delay in milliseconds.
   */
  private getSaleCalculationDelay(): number {
    let calculationDelay = 250

    if (env.isTest() || !this.syncManager.hasActivePromotions) {
      calculationDelay = 0
    }

    return calculationDelay
  }

  /**
   * Sets the pickable state for a sale
   */
  public setPickableState() {
    const { getState, dispatch } = this.$ngRedux
    return new Promise<void>(resolve => {
      const currentSale = selectCurrentSaleAsSale(getState())
      // @todo Update once fulfill_from_another_outlet is fully rolled out
      const outletId =
        currentSale.fulfillmentOutletID ||
        this.registerManager.getActiveOutlet().id

      isSalePickable(currentSale.lineItems, outletId)
        .then(isPickable => {
          dispatch(updatePickable(isPickable))
          resolve()
        })
        .catch(() => {
          // Default to false
          dispatch(updatePickable(false))
          resolve()
        })
    })
  }

  /**
   * Determine if we should display the email error validation banner
   *
   * @method shouldDisplayEmailErrorBanner
   *
   * @return {Boolean} True if the email error validation banner should be shown, false otherwise
   */
  public shouldDisplayEmailErrorBanner() {
    const { receiptEmailAddress } = selectCurrentSale(this.$ngRedux.getState())
    const shouldDisplayEmailError =
      this.webRegisterStatusManager.isOnline() &&
      !!receiptEmailAddress &&
      !isValidEmail(receiptEmailAddress)

    return shouldDisplayEmailError
  }

  /**
   * Finds and removes an EcomCustomCharge by sourceId.
   *
   * @param sourceId - The sourceId of the charge to remove.
   * @return {boolean} - Returns true if the charge was found and removed, false otherwise.
   */
  public removeEcomCustomCharge = (sourceId: string): boolean => {
    const { getState, dispatch } = this.$ngRedux
    const saleFinals = selectSaleFinals(getState())

    if (!saleFinals.ecomCustomCharges?.charges) {
      return false // No charges to remove
    }

    const chargeIndex = saleFinals.ecomCustomCharges.charges.findIndex(
      charge => charge.sourceId === sourceId
    )

    if (chargeIndex === -1) {
      return false // Charge not found
    }

    // Dispatch an update to the Redux store
    dispatch(removeEcomCustomCharge(sourceId))
    this.queueSaleCalculation()

    return true // Charge successfully removed
  }

  /**
   * check if EcomCustomCharges is enable and exist
   *
   * @returns {boolean} - Returns true if there are EcomCustomSurcharges, false otherwise.
   */
  public hasEcomCustomSurcharges() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState())
    const isEcomCustomSurchargesEnabled = this.vdFeatureManager.get(
      FEATURE_ADD_ECOM_CUSTOM_CHARGES_ROW
    )
    return (
      isEcomCustomSurchargesEnabled &&
      currentSale.ecomCustomCharges &&
      !vn(currentSale.ecomCustomCharges?.totalIncl).eq(0)
    )
  }
}

angular.module('webregister.managers').factory('saleManager', SaleManager)
